import type { Matrix4 } from 'three';
import type { LifeModel } from './models';

/** An affine lean keeps the joint attached while its flat sole follows the planted foot path. */
export function quadrupedLegMatrix(
  target: Matrix4,
  model: LifeModel,
  distance: number,
  weight: number,
  fore: boolean,
  side: -1 | 1
): Matrix4 {
  const offset = fore
    ? side < 0
      ? 0
      : 0.5
    : side < 0
      ? model.hindPhase
      : (model.hindPhase + 0.5) % 1;
  const phase = (((distance / model.strideM + offset) % 1) + 1) % 1;
  const contact = 0.6;
  const reach = (model.strideM * contact) / 2;
  const amount = Math.min(1, Math.max(0, weight) * 4);
  let forward = reach * (1 - (2 * phase) / contact),
    lift = 0;
  if (phase > contact) {
    const s = (phase - contact) / (1 - contact);
    const ease = s * s * (3 - 2 * s);
    const tangent = (-2 * reach * (1 - contact)) / contact;
    forward = reach * (2 * ease - 1) + tangent * s * (2 * s - 1) * (s - 1);
    lift = model.stepLiftM * Math.sin(Math.PI * s) ** 2;
  }
  forward *= amount;
  lift *= amount;
  const bottom = model.sole[2];
  const hip = fore ? model.shoulderY : model.hipY;
  return target.set(
    1,
    forward / bottom,
    0,
    fore ? model.foreX : model.hindX,
    0,
    (lift - hip) / bottom,
    0,
    hip,
    0,
    0,
    1,
    side * (fore ? model.shoulderHalf : model.hipHalf),
    0,
    0,
    0,
    1
  );
}
